import * as React from 'react';

import CourseImageDesktop from '@/features/user-notifications/assets/course-desktop.svg';
import CourseImageMobile from '@/features/user-notifications/assets/course-mobile.svg';
import { Button } from '@/ui/button';
import { Dropdown } from '@/ui/dropdown';
import type { DropdownOption } from '@/ui/dropdown';

import type { NaplanBatch } from '../../api/get-batches';

interface EnrollmentFormProps {
  selectedGrade: string;
  setSelectedGrade: (grade: string) => void;
  isPending: boolean;
  handleEnroll: () => void;
  isMobile: boolean;
  description?: string;
  availableGrades?: string[];
  // Batch selection props for NAPLAN
  showBatchSelection?: boolean;
  batches?: NaplanBatch[];
  selectedBatch?: NaplanBatch | null;
  setSelectedBatch?: (batch: NaplanBatch | null) => void;
  isLoadingBatches?: boolean;
  isBatchesError?: boolean;
  onBatchSelect?: (batch: NaplanBatch | null) => void;
}

const formatBatchOption = (batch: NaplanBatch): DropdownOption => {
  const startDate = new Date(batch.start_date).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const schedule = batch.course_session_info?.schedule?.[0];
  const day = schedule?.day ?? '';
  const time = schedule?.time ?? '';
  const label = `${startDate} - ${day} (${time})`;
  return {
    value: batch.batch_id,
    label,
  };
};

interface BatchSelectionSectionProps {
  showBatchSelection: boolean;
  selectedGrade: string;
  isMobile: boolean;
  batchValue: string;
  handleBatchChange: (value: string) => void;
  isLoadingBatches: boolean;
  batchOptions: DropdownOption[];
  isPending: boolean;
  batchesLength: number;
  isBatchesError: boolean;
}

const BatchSelectionSection: React.FC<BatchSelectionSectionProps> = ({
  showBatchSelection,
  selectedGrade,
  isMobile,
  batchValue,
  handleBatchChange,
  isLoadingBatches,
  batchOptions,
  isPending,
  batchesLength,
  isBatchesError,
}) => {
  if (!showBatchSelection || !selectedGrade) return null;

  return (
    <>
      <div className="flex justify-center mb-2">
        <div className="w-[200px] md:w-[250px]">
          <Dropdown
            id="batch-select"
            value={batchValue}
            onValueChange={handleBatchChange}
            placeholder={
              isLoadingBatches ? 'Loading batches...' : 'Select Batch'
            }
            options={batchOptions}
            disabled={isPending || isLoadingBatches || batchesLength === 0}
            size={isMobile ? 'sm' : 'default'}
          />
        </div>
      </div>

      {/* Batch Loading Error Message */}
      {isBatchesError && !isLoadingBatches ? (
        <div className="text-center text-sm text-red-400 mb-4">
          Failed to load batches. Please try again.
        </div>
      ) : (
        <div className="mb-4" />
      )}
    </>
  );
};

export const EnrollmentForm: React.FC<EnrollmentFormProps> = ({
  selectedGrade,
  setSelectedGrade,
  isPending,
  handleEnroll,
  isMobile,
  description = 'New course unlocked!!! Enjoy access to a tailored course',
  availableGrades,
  showBatchSelection = false,
  batches = [],
  selectedBatch,
  setSelectedBatch,
  isLoadingBatches = false,
  isBatchesError = false,
  onBatchSelect,
}) => {
  // Convert grades to dropdown options
  const gradeOptions: DropdownOption[] = React.useMemo(
    () =>
      (availableGrades ?? []).map(grade => ({
        value: grade,
        label: `Grade ${grade}`,
      })),
    [availableGrades]
  );

  // Convert batches to dropdown options
  const batchOptions: DropdownOption[] = React.useMemo(() => {
    if (!batches || batches.length === 0) return [];
    return batches.map(batch => formatBatchOption(batch));
  }, [batches]);

  const handleBatchChange = (value: string) => {
    const batch = batches.find(b => b.batch_id === value) ?? null;
    setSelectedBatch?.(batch);
    onBatchSelect?.(batch);
  };

  const batchValue = selectedBatch?.batch_id ?? '';

  return (
    <>
      {/* Loading Progress */}
      {isPending ? (
        <div className="w-full h-1 bg-magical-moonlight-d2 rounded overflow-hidden mb-4">
          <div className="h-full bg-blue-chaos animate-pulse w-full" />
        </div>
      ) : null}

      {/* Course Banner Image */}
      <div className="w-full overflow-hidden mb-6 rounded-2xl relative">
        {/* Mobile Image */}
        <img
          src={CourseImageMobile}
          alt="course img"
          className="w-full h-25 object-cover md:hidden"
        />
        {/* Desktop Image */}
        <img
          src={CourseImageDesktop}
          alt="course img"
          className="w-full h-36 object-contain lg:object-cover hidden md:block"
        />
        {/* Text Overlay */}
        <div className="absolute inset-0 flex items-center justify-start pl-6">
          <h3
            className="text-white text-sm lg:text-3xl font-bold leading-10 capitalize"
            style={{ fontFamily: 'Quicksand' }}
          >
            New Course Unlocked
          </h3>
        </div>
      </div>

      {/* Content Text */}
      <div className="text-center mb-8">
        <p className="text-[10px] md:text-sm">{description}</p>
      </div>

      {/* Grade Selection Dropdown */}
      <div className="flex justify-center mb-6">
        <div className="w-50 md:w-60">
          <Dropdown
            id="grade-select"
            value={selectedGrade}
            onValueChange={setSelectedGrade}
            placeholder="Select Grade"
            options={gradeOptions}
            disabled={isPending}
            size={isMobile ? 'sm' : 'default'}
          />
        </div>
      </div>

      {/* Batch Selection Dropdown (for NAPLAN) */}
      <BatchSelectionSection
        showBatchSelection={showBatchSelection}
        selectedGrade={selectedGrade}
        isMobile={isMobile}
        batchValue={batchValue}
        handleBatchChange={handleBatchChange}
        isLoadingBatches={isLoadingBatches}
        batchOptions={batchOptions}
        isPending={isPending}
        batchesLength={batches.length}
        isBatchesError={isBatchesError}
      />

      {/* Enroll Button */}
      <div className="flex justify-center">
        <Button
          onClick={handleEnroll}
          disabled={
            !selectedGrade ||
            isPending ||
            (showBatchSelection && !selectedBatch)
          }
          variant="contained"
          size="sm"
          isLoading={isPending}
          className="w-40 h-9"
        >
          Enroll Now
        </Button>
      </div>
    </>
  );
};
